import React from "react";
import { Link, NavLink, Navigate, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { api, inr, fileUrl, isOnSale, openPdf, errorMessage, uploadImage } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Users, Package, Boxes, ShoppingBag, Palette, Store, BarChart3, Settings, LogOut, Plus, Trash2, ScanLine, Tag, Camera, X, MessageCircle, Phone, Edit2, LayoutTemplate, Menu, Star, Newspaper, FileText, RotateCcw } from "lucide-react";
import { Logo } from "@/components/Logo";
import AdminHomeContent from "@/pages/AdminHomeContent";
import AdminBlog from "@/pages/AdminBlog";
import AdminCMS from "@/pages/AdminCMS";
import Orders, { ShippingSettings } from "@/pages/AdminOrders";
import AdminReturns from "@/pages/AdminReturns";
import Inventory from "@/pages/AdminInventory";
import { Pagination, usePaged } from "@/components/Pagination";
import { waHref } from "@/lib/site";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, PieChart, Pie, Cell } from "recharts";

const ROLE_LABEL = { super_admin: "Super Admin", major_admin: "Major Admin", admin: "Admin", designer: "Designer", sales: "Sales", customer: "Customer" };

export default function AdminApp() {
  const { user, logout } = useAuth();
  const [menu, setMenu] = React.useState(false);
  const { pathname } = useLocation();
  React.useEffect(() => { setMenu(false); }, [pathname]);
  if (user === null) return <div className="p-20 text-center">Loading...</div>;
  if (!user) return <Navigate to="/login?redirect=/admin" replace/>;
  if (user.role === "customer") return <div className="p-20 text-center">Access denied. <Link to="/" className="underline">Home</Link></div>;

  const items = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, perms: ["*"] },
    { to: "/admin/products", label: "Products", icon: Package, perms: ["products.manage"] },
    { to: "/admin/categories", label: "Categories", icon: Tag, perms: ["categories.manage"] },
    { to: "/admin/inventory", label: "Inventory", icon: Boxes, perms: ["inventory.manage"] },
    { to: "/admin/scanner", label: "Barcode Scanner", icon: ScanLine, perms: ["inventory.manage"] },
    { to: "/admin/orders", label: "Orders & Shipping", icon: ShoppingBag, perms: ["orders.manage"] },
    { to: "/admin/returns", label: "Returns", icon: RotateCcw, perms: ["orders.manage"] },
    { to: "/admin/customizations", label: "Customizations", icon: Palette, perms: ["customizations.manage", "designer.workflow"] },
    { to: "/admin/pos", label: "POS-lite", icon: Store, perms: ["sales.pos"] },
    { to: "/admin/statistics", label: "Statistics", icon: BarChart3, perms: ["statistics.view"] },
    { to: "/admin/customers", label: "Customers CRM", icon: MessageCircle, perms: ["crm.manage"] },
    { to: "/admin/users", label: "Users & Roles", icon: Users, perms: ["users.manage"] },
    { to: "/admin/reviews", label: "Reviews", icon: Star, perms: ["products.manage"] },
    { to: "/admin/coupons", label: "Coupons", icon: Tag, perms: ["coupons.manage"] },
    { to: "/admin/content", label: "Website Content (CMS)", icon: FileText, perms: ["__super_admin_only"] },
    { to: "/admin/home-page", label: "Home Page", icon: LayoutTemplate, perms: ["__super_admin_only"] },
    { to: "/admin/blog", label: "Blog", icon: Newspaper, perms: ["__super_admin_only"] },
    { to: "/admin/settings", label: "Settings", icon: Settings, perms: ["settings.manage"] },
  ];
  const canSee = (perms) => user.role === "super_admin" || perms.includes("*") || perms.some(p => (user.permissions || []).includes(p));

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[240px_1fr] bg-[#FAF9F6]">
      {/* Phone / tablet top bar — menu button on the right */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b flex items-center justify-between px-4 h-16">
        <Logo size={38} subtitle="Admin Console"/>
        <button onClick={() => setMenu(true)} aria-label="Open admin menu" data-testid="admin-menu-toggle" className="p-2 rounded-full hover:bg-neutral-100"><Menu size={22}/></button>
      </div>
      {menu && <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMenu(false)}/>}
      <aside className={`bg-white border-r border-neutral-200 flex flex-col fixed lg:sticky top-0 right-0 lg:right-auto h-screen w-[82%] max-w-[280px] lg:w-auto lg:max-w-none z-50 transition-transform duration-300 ${menu ? "translate-x-0" : "translate-x-full"} lg:translate-x-0`}>
        <div className="p-6 border-b flex items-center justify-between">
          <Logo size={44} subtitle="Admin Console"/>
          <button onClick={() => setMenu(false)} aria-label="Close admin menu" className="lg:hidden p-1.5 rounded-full hover:bg-neutral-100"><X size={20}/></button>
        </div>
        <div className="p-6 border-b">
          <div className="text-xs text-neutral-500">Signed in as</div>
          <div className="font-semibold text-sm">{user.name}</div>
          <span className="inline-block mt-2 text-[10px] uppercase tracking-widest px-2 py-1 bg-[#E8F3EE] text-[#0E4D34]">{ROLE_LABEL[user.role]}</span>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {items.filter(i => canSee(i.perms)).map(i => (
            <NavLink key={i.to} to={i.to} end={i.to === "/admin"} data-testid={`admin-nav-${i.label.toLowerCase().replace(/\s+/g,'-')}`}
                     className={({ isActive }) => `flex items-center gap-3 px-6 py-2.5 text-sm hover:bg-neutral-50 ${isActive ? "text-[#0E4D34] border-l-2 border-[#0E4D34] bg-[#E8F3EE]/50 font-semibold" : "text-neutral-700"}`}>
              <i.icon size={16}/>{i.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-6 border-t space-y-2">
          <Link to="/" className="flex items-center gap-2 text-xs text-neutral-500 hover:text-[#0E4D34]">← View Storefront</Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-[#991B1B]" data-testid="admin-logout"><LogOut size={12}/>Sign out</button>
        </div>
      </aside>
      <main className="p-4 sm:p-6 lg:p-10 min-w-0">
        <Routes>
          <Route index element={<Dashboard/>}/>
          <Route path="products" element={<Products/>}/>
          <Route path="products/new" element={<ProductForm/>}/>
          <Route path="products/:id" element={<ProductForm/>}/>
          <Route path="categories" element={<Categories/>}/>
          <Route path="inventory" element={<Inventory/>}/>
          <Route path="scanner" element={<Scanner/>}/>
          <Route path="orders" element={<Orders/>}/>
          <Route path="returns" element={<AdminReturns/>}/>
          <Route path="customizations" element={<AdminCustomizations/>}/>
          <Route path="pos" element={<POS/>}/>
          <Route path="statistics" element={<Statistics/>}/>
          <Route path="customers" element={<CustomersCRM/>}/>
          <Route path="customers/:id" element={<CustomerDetailCRM/>}/>
          <Route path="users" element={<UsersAdmin/>}/>
          <Route path="reviews" element={<ReviewsAdmin/>}/>
          <Route path="coupons" element={<Coupons/>}/>
          <Route path="content" element={<AdminCMS/>}/>
          <Route path="home-page" element={<AdminHomeContent/>}/>
          <Route path="blog" element={<AdminBlog/>}/>
          <Route path="settings" element={<AdminSettings/>}/>
        </Routes>
      </main>
    </div>
  );
}

// ---------- DASHBOARD ----------
function Dashboard() {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/statistics").then(r => r.data).catch(() => null) });
  return (
    <div>
      <h1 className="h2-editorial mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Revenue" value={inr(stats?.revenue || 0)}/>
        <Stat label="Orders" value={stats?.order_count || 0}/>
        <Stat label="Customers" value={stats?.customers || 0}/>
        <Stat label="In Stock" value={stats?.pieces_in_stock || 0}/>
      </div>
      {stats?.daily_revenue?.length > 0 && (
        <div className="bg-white border p-6">
          <h3 className="font-semibold mb-4">Recent Revenue</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={stats.daily_revenue}>
              <XAxis dataKey="date"/><YAxis/><Tooltip/>
              <Line type="monotone" dataKey="revenue" stroke="#0E4D34" strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
function Stat({ label, value }) {
  return <div className="bg-white border p-6"><div className="overline mb-2">{label}</div><div className="font-serif text-3xl text-[#0E4D34]">{value}</div></div>;
}

// ---------- PRODUCTS ----------
function Products() {
  const { data, refetch, isLoading } = useQuery({ queryKey: ["adm-products"], queryFn: loadAdminProducts });
  const [q, setQ] = React.useState("");
  const list = (data || []).filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.category_name || "").toLowerCase().includes(q.toLowerCase()));
  const pg = usePaged(list, 20, { resetKey: q });
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Its barcode pieces will be deleted too.`)) return;
    try { await api.delete(`/products/${p.id}`); refetch(); toast.success("Product deleted"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div>
      <div className="flex justify-between items-center gap-3 mb-6 flex-wrap">
        <h1 className="h2-editorial">Products</h1>
        <div className="flex gap-2 flex-1 sm:flex-none justify-end">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products" className="border border-neutral-300 p-2.5 text-sm w-full sm:w-60 focus:outline-none focus:border-[#0E4D34]"/>
          <Link to="/admin/products/new" className="btn-silk whitespace-nowrap" data-testid="btn-new-product"><Plus size={12} className="mr-1"/>New</Link>
        </div>
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["Product","Category","Price","Discount","Stock","Badges","Status",""].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="p-6 text-center text-neutral-500">Loading…</td></tr>}
            {!isLoading && list.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-neutral-500">No products.</td></tr>}
            {pg.items.map(p => (
              <tr key={p.id} className="border-t hover:bg-neutral-50/60" data-testid={`row-product-${p.slug}`}>
                <td className="p-3">
                  <Link to={`/admin/products/${p.id}`} className="flex items-center gap-3 hover:text-[#0E4D34] font-semibold">
                    {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt="" className="w-10 h-12 object-cover rounded"/> : <span className="w-10 h-12 rounded bg-neutral-100"/>}
                    <span>{p.name}</span>
                  </Link>
                </td>
                <td className="p-3 text-neutral-600">{p.category_name || <span className="text-[#991B1B]">—</span>}</td>
                <td className="p-3 whitespace-nowrap">{inr(p.price)}{p.compare_at_price ? <span className="text-neutral-400 line-through text-xs ml-1.5">{inr(p.compare_at_price)}</span> : null}</td>
                <td className="p-3"><DiscountCell product={p} onSaved={refetch}/></td>
                <td className="p-3">{p.stock}</td>
                <td className="p-3"><div className="flex flex-wrap gap-1">
                  {isOnSale(p) && <span className="bg-[#991B1B] text-white text-[9px] tracking-widest uppercase px-1.5 py-0.5">Sale</span>}
                  {p.featured && <span className="bg-[#0E4D34] text-white text-[9px] tracking-widest uppercase px-1.5 py-0.5">Featured</span>}
                  {p.badge && <span className="bg-[#D4AF37] text-neutral-900 text-[9px] tracking-widest uppercase px-1.5 py-0.5">{p.badge}</span>}
                </div></td>
                <td className="p-3">{p.active ? <span className="text-[10px] uppercase tracking-widest text-[#0E4D34]">Live</span> : <span className="text-[10px] uppercase tracking-widest text-neutral-400">Hidden</span>}</td>
                <td className="p-3">
                  <div className="flex items-center gap-3 justify-end">
                    <Link to={`/admin/products/${p.id}`} className="text-xs border px-3 py-1.5 flex items-center gap-1 hover:border-[#0E4D34]" data-testid={`edit-product-${p.slug}`}><Edit2 size={12}/>Edit</Link>
                    <button onClick={() => remove(p)} aria-label="Delete product" className="text-[#991B1B] p-1.5"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pg.props} label="products"/>
    </div>
  );
}

/** Discount % typed straight into the product list — saved when you press Enter or leave the box. */
function DiscountCell({ product, onSaved }) {
  const qc = useQueryClient();
  const current = product.discount_percent ?? (product.compare_at_price && Number(product.compare_at_price) > Number(product.price)
    ? Math.round((1 - product.price / product.compare_at_price) * 1000) / 10 : 0);
  const [v, setV] = React.useState(String(current || ""));
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setV(String(current || "")); }, [current]);
  const save = async () => {
    const n = Number(v || 0);
    if (n === Number(current || 0)) return;
    if (n < 0 || n > 90 || Number.isNaN(n)) { toast.error("Discount must be between 0 and 90 %"); setV(String(current || "")); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/products/${product.id}/discount`, { discount_percent: n });
      toast.success(n ? `${product.name}: ${n}% off — now ${inr(r.data.price)}` : `${product.name}: discount removed`);
      onSaved?.(); qc.invalidateQueries({ queryKey: ["shop-all"] });
    } catch (e) { toast.error(errorMessage(e)); setV(String(current || "")); } finally { setBusy(false); }
  };
  return (
    <div className="relative w-20">
      <input value={v} onChange={e => setV(e.target.value.replace(/[^\d.]/g, ""))} onBlur={save} onKeyDown={e => e.key === "Enter" && e.currentTarget.blur()}
             disabled={busy} placeholder="0" inputMode="decimal" aria-label={`Discount for ${product.name}`} data-testid={`disc-${product.slug}`}
             className={`w-full border p-1.5 pr-6 text-sm ${Number(v) > 0 ? "border-[#991B1B]/40 text-[#991B1B] font-semibold" : "border-neutral-300"}`}/>
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400">%</span>
    </div>
  );
}

// Works with the new backend (/admin/products, includes hidden products) and falls back to the public list.
async function loadAdminProducts() {
  try { return (await api.get("/admin/products")).data; }
  catch (e) {
    if (e?.response?.status === 404) return (await api.get("/products?limit=500")).data;
    throw e;
  }
}
const toSlug = (t) => (t || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "item";

const EMPTY_PRODUCT = { stock_qty: 1, name: "", category_id: "", description: "", mrp: "", discount_percent: "", fabric: "", color: "", occasion: "", images: [], featured: false, badge: "", active: true };
/** selling price after a % discount (whole rupees) — same rule as the server */
export const discounted = (mrp, pct) => { const m = Number(mrp) || 0; const d = Math.min(90, Math.max(0, Number(pct) || 0)); return d > 0 ? Math.round(m * (1 - d / 100)) : m; };

function ProductForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then(r => r.data) });
  const { data: existing, isLoading, error: loadError } = useQuery({
    queryKey: ["adm-p", id], enabled: !!id, retry: false,
    queryFn: async () => {
      try { return (await api.get(`/admin/products/${id}`)).data; }
      catch (e) {
        if (e?.response?.status !== 404) throw e;
        const all = await loadAdminProducts();
        const p = all.find(x => x.id === id);
        if (!p) throw e;
        return p;
      }
    },
  });
  const [f, setF] = React.useState(EMPTY_PRODUCT);
  const [busy, setBusy] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  React.useEffect(() => {
    if (!existing) return;
    const fill = (v) => (v === null || v === undefined ? "" : v);
    setF({
      ...EMPTY_PRODUCT,
      ...Object.fromEntries(Object.keys(EMPTY_PRODUCT).map(k => [k, fill(existing[k])])),
      stock_qty: 0,
      images: existing.images || [], featured: !!existing.featured, active: existing.active !== false,
      // older products: MRP = the struck-out price (or the price), discount worked out from it
      mrp: existing.mrp ?? (isOnSale(existing) && Number(existing.compare_at_price) > Number(existing.price) ? existing.compare_at_price : existing.price),
      discount_percent: existing.discount_percent ?? (isOnSale(existing) && Number(existing.compare_at_price) > Number(existing.price)
        ? Math.round((1 - existing.price / existing.compare_at_price) * 1000) / 10 : 0),
    });
  }, [existing]);

  const upload = async (e) => {
    const files = [...e.target.files]; e.target.value = "";
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const url = await uploadImage(file, "products");
        setF(fx => ({ ...fx, images: [...fx.images, url] }));
        ok++;
      } catch (err) { toast.error(`${file.name}: ${err.message}`); }
    }
    if (ok) toast.success(`${ok} image${ok > 1 ? "s" : ""} uploaded`);
    setUploading(false);
  };
  const moveImage = (i, d) => setF(fx => { const im = [...fx.images]; const j = i + d; if (j < 0 || j >= im.length) return fx; [im[i], im[j]] = [im[j], im[i]]; return { ...fx, images: im }; });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category_id) return toast.error("Please choose a category");
    if (f.mrp === "" || Number(f.mrp) <= 0) return toast.error("Please enter the price (MRP)");
    if (Number(f.discount_percent) < 0 || Number(f.discount_percent) > 90) return toast.error("Discount must be between 0 and 90 %");
    if (!f.name.trim()) return toast.error("Please enter the product name");
    const payload = {
      name: f.name.trim(), category_id: f.category_id,
      price: Number(f.mrp), discount_percent: Number(f.discount_percent) || 0,   // server works out the selling price
      description: (f.description || "").trim(),
      fabric: (f.fabric || "").trim() || null, color: (f.color || "").trim() || null, occasion: (f.occasion || "").trim() || null,
      images: f.images, featured: !!f.featured, active: !!f.active,
      badge: (f.badge || "").trim() || null,
      // The current backend makes the web address itself; this is only for an older backend that still requires it.
      slug: existing?.slug || `${toSlug(f.name)}-${Date.now().toString(36).slice(-4)}`,
    };
    setBusy(true);
    try {
      const saved = id ? (await api.patch(`/products/${id}`, payload)).data : (await api.post("/products", payload)).data;
      // Stock = barcode pieces. Create the requested number so the product can be bought right away.
      const qty = Math.max(0, Math.min(500, parseInt(f.stock_qty, 10) || 0));
      if (qty > 0 && saved?.id) {
        try { await api.post("/pieces", { product_id: saved.id, count: qty }); }
        catch (err) { toast.error(`Product saved, but stock pieces couldn't be created: ${errorMessage(err)}`); }
      }
      qc.invalidateQueries({ queryKey: ["adm-products"] }); qc.invalidateQueries({ queryKey: ["shop-all"] }); qc.invalidateQueries({ queryKey: ["adm-p", id] });
      toast.success(id ? "Product updated" : "Product added");
      nav("/admin/products");
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  if (id && isLoading) return <div className="text-neutral-500">Loading…</div>;
  if (id && loadError) return <div className="bg-white border p-6 text-sm">Couldn't load this product: {errorMessage(loadError)} <Link to="/admin/products" className="underline ml-1">Back</Link></div>;
  const sellPrice = discounted(f.mrp, f.discount_percent);
  const hasDiscount = Number(f.discount_percent) > 0 && Number(f.mrp) > 0;
  return (
    <div>
      <Link to="/admin/products" className="text-xs text-neutral-500 hover:text-[#0E4D34] mb-2 inline-block">← All products</Link>
      <h1 className="h2-editorial mb-6">{id ? "Edit" : "New"} Product</h1>
      <form onSubmit={submit} className="bg-white border p-5 sm:p-8 space-y-5 max-w-3xl">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Input label="Product name *" value={f.name} onChange={v => setF({...f, name: v})} testid="p-name"/></div>
          <Select label="Category *" value={f.category_id} onChange={v => setF({...f, category_id: v})} testid="p-cat">
            <option value="">Select…</option>
            {(cats || []).filter(c => !c.parent_id).map(c => (
              <React.Fragment key={c.id}>
                <option value={c.id}>{c.name}</option>
                {(cats || []).filter(s => s.parent_id === c.id).map(s => <option key={s.id} value={s.id}>— {s.name}</option>)}
              </React.Fragment>
            ))}
          </Select>
          <Input label="Price (MRP) ₹ *" type="number" value={f.mrp} onChange={v => setF({...f, mrp: v})} testid="p-price"/>
          <div className="sm:col-span-2 border border-[#0E4D34]/20 bg-[#F4FAF7] p-4 grid sm:grid-cols-[180px_1fr] gap-4 items-center" data-testid="p-discount-box">
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">Discount %</label>
              <div className="relative">
                <input type="number" min="0" max="90" step="0.5" value={f.discount_percent} onChange={e => setF({...f, discount_percent: e.target.value})} placeholder="0"
                       data-testid="p-discount" className="w-full border border-neutral-300 p-2.5 pr-8 text-sm bg-white focus:outline-none focus:border-[#0E4D34] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">%</span>
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">{[0, 10, 15, 20, 25, 30].map(n => (
                <button type="button" key={n} onClick={() => setF({...f, discount_percent: n})} className={`text-[11px] px-2 py-0.5 border ${Number(f.discount_percent || 0) === n ? "bg-[#0E4D34] text-white border-[#0E4D34]" : "bg-white"}`}>{n ? `${n}%` : "None"}</button>
              ))}</div>
            </div>
            <div className="text-sm" data-testid="p-price-preview">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Customers see</div>
              {Number(f.mrp) > 0 ? (
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-2xl font-semibold text-[#0E4D34]">{inr(sellPrice)}</span>
                  {hasDiscount && <><span className="text-neutral-400 line-through">{inr(f.mrp)}</span>
                    <span className="text-[11px] font-bold bg-[#991B1B] text-white px-1.5 py-0.5">{Number(f.discount_percent)}% OFF</span>
                    <span className="text-xs text-neutral-500 basis-full">You give ₹{(Number(f.mrp) - sellPrice).toLocaleString("en-IN")} off · the SALE badge shows automatically</span></>}
                </div>
              ) : <span className="text-neutral-400">Enter the price to see the preview</span>}
            </div>
          </div>
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3 items-end">
            <div>
              <Input label={id ? "Add more pieces to stock" : "Pieces in stock *"} type="number" value={f.stock_qty} onChange={v => setF({...f, stock_qty: v})} testid="p-stock"/>
            </div>
            <p className="text-[11px] text-neutral-500 pb-2">
              {id ? <>Currently <b>{existing?.stock ?? 0}</b> in stock. </> : null}
              Each piece gets its own barcode (see Inventory). With 0 pieces the product shows <b>Sold out</b>.
            </p>
          </div>
          <Input label="Fabric (optional)" value={f.fabric} onChange={v => setF({...f, fabric: v})} testid="p-fabric"/>
          <Input label="Colour (optional)" value={f.color} onChange={v => setF({...f, color: v})} testid="p-color"/>
          <div className="sm:col-span-2 sm:max-w-[calc(50%-0.375rem)]"><Input label="Occasion (optional)" value={f.occasion} onChange={v => setF({...f, occasion: v})} testid="p-occasion"/></div>
        </div>
        <div><label className="text-xs uppercase tracking-widest text-neutral-600 mb-1.5 block">Description <span className="normal-case tracking-normal text-neutral-400">(optional)</span></label><textarea rows={4} value={f.description} onChange={e => setF({...f, description: e.target.value})} data-testid="p-desc" className="w-full border border-neutral-300 p-3 text-sm focus:outline-none focus:border-[#0E4D34]"/></div>
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-600 mb-1.5 block">Images <span className="normal-case tracking-normal text-neutral-400">(first image is the main photo)</span></label>
          <label className="btn-outline-silk cursor-pointer">
            {uploading ? "Uploading…" : "Upload images"}
            <input type="file" multiple accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={upload} className="hidden" data-testid="p-images"/>
          </label>
          <div className="flex gap-3 mt-3 flex-wrap">{f.images.map((im, i) => (
            <div key={im + i} className="relative group">
              <img src={fileUrl(im)} alt="" className={`w-20 h-24 object-cover rounded border ${i === 0 ? "ring-2 ring-[#0E4D34]" : ""}`}/>
              <button type="button" aria-label="Remove image" onClick={() => setF({...f, images: f.images.filter((_, x) => x !== i)})} className="absolute -top-2 -right-2 bg-[#991B1B] text-white rounded-full p-1"><X size={10}/></button>
              <div className="flex justify-between mt-1">
                <button type="button" disabled={i === 0} onClick={() => moveImage(i, -1)} className="text-xs px-1 disabled:opacity-20">←</button>
                <button type="button" disabled={i === f.images.length - 1} onClick={() => moveImage(i, 1)} className="text-xs px-1 disabled:opacity-20">→</button>
              </div>
            </div>
          ))}</div>
        </div>
        <div className="border border-neutral-200 p-4 space-y-3 bg-neutral-50/50">
          <div className="text-xs uppercase tracking-widest text-neutral-600">Badges & visibility</div>
          <label className="flex items-start gap-2"><input type="checkbox" className="mt-1" checked={!!f.featured} onChange={e => setF({...f, featured: e.target.checked})} data-testid="p-featured"/>
            <span><span className="inline-block bg-[#0E4D34] text-white text-[10px] tracking-widest uppercase px-2 py-0.5 mr-2">Featured</span>Featured <span className="text-xs text-neutral-500">— shows the FEATURED badge and lists it in Products in Limelight on the home page</span></span></label>
          <div className="max-w-sm"><Input label="Custom badge (optional, e.g. New Arrival, Bestseller)" value={f.badge} onChange={v => setF({...f, badge: v.slice(0, 24)})} testid="p-badge"/></div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.active} onChange={e => setF({...f, active: e.target.checked})} data-testid="p-active"/>Active <span className="text-xs text-neutral-500">— visible in the shop (untick to hide without deleting)</span></label>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={busy || uploading} className="btn-silk disabled:opacity-50" data-testid="p-save">{busy ? "Saving…" : id ? "Save changes" : "Add product"}</button>
          <Link to="/admin/products" className="btn-outline-silk">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

// ---------- CATEGORIES ----------
const EMPTY_CAT = { name: "", prefix: "", parent_id: "", image_url: "", description: "", show_in_menu: false, menu_order: "" };

function Categories() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["cats-admin"], queryFn: () => api.get("/categories").then(r => r.data) });
  const { data: products } = useQuery({ queryKey: ["adm-products"], queryFn: loadAdminProducts });
  const [f, setF] = React.useState(EMPTY_CAT);
  const [editId, setEditId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const cats = data || [];
  const count = (cid) => (products || []).filter(p => p.category_id === cid).length;

  const reset = () => { setF(EMPTY_CAT); setEditId(null); };
  const startEdit = (c) => {
    setEditId(c.id);
    setF({ name: c.name || "", prefix: c.prefix || "", parent_id: c.parent_id || "", image_url: c.image_url || "", description: c.description || "",
           show_in_menu: !!c.show_in_menu, menu_order: c.menu_order ?? "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file, "categories"); setF(x => ({ ...x, image_url: url })); }
    catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Please enter a category name");
    if (f.prefix && (f.prefix.length < 2 || f.prefix.length > 7)) return toast.error("Barcode prefix must be 2 to 7 characters");
    setBusy(true);
    try {
      const current = cats.find(c => c.id === editId);
      const payload = { ...f, name: f.name.trim(), parent_id: f.parent_id || null, prefix: f.prefix || null,
        show_in_menu: !!f.show_in_menu, menu_order: f.menu_order === "" ? null : Number(f.menu_order),
        slug: current?.slug || toSlug(f.name) };  // only used by an older backend
      if (editId) await api.patch(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(editId ? "Category updated" : "Category added");
      reset(); refetch(); qc.invalidateQueries({ queryKey: ["cats"] });
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Category deleted"); if (editId === c.id) reset(); refetch(); qc.invalidateQueries({ queryKey: ["cats"] }); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const parents = cats.filter(c => !c.parent_id);
  const ordered = parents.flatMap(p => [p, ...cats.filter(s => s.parent_id === p.id)]);

  return (
    <div>
      <h1 className="h2-editorial mb-6">Categories</h1>
      <div className="grid lg:grid-cols-[minmax(0,420px)_1fr] gap-6 items-start">
        <form onSubmit={save} className={`bg-white border p-6 space-y-4 ${editId ? "border-[#0E4D34]" : ""}`} data-testid="cat-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editId ? "Edit Category" : "Add Category"}</h3>
            {editId && <button type="button" onClick={reset} className="text-xs text-neutral-500 hover:text-[#991B1B] flex items-center gap-1"><X size={12}/>Cancel edit</button>}
          </div>
          <Input label="Name *" value={f.name} onChange={v => setF({...f, name: v})} testid="cat-name"/>
          <div>
            <Input label="Barcode prefix (2–7 letters / numbers)" value={f.prefix} onChange={v => setF({...f, prefix: v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7)})} testid="cat-prefix"/>
            <p className="text-[11px] text-neutral-500 mt-1">{f.prefix ? <>Barcodes will look like <span className="font-mono text-neutral-800">{f.prefix}00001</span></> : "Used at the start of every barcode in this category, e.g. SR → SR00001"}{editId ? ". Changing it only affects new barcodes." : ""}</p>
          </div>
          <Select label="Parent category (optional)" value={f.parent_id} onChange={v => setF({...f, parent_id: v})} testid="cat-parent">
            <option value="">None — this is a main category</option>
            {parents.filter(c => c.id !== editId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <div>
            <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">Image</label>
            <div className="flex items-center gap-3 flex-wrap">
              {f.image_url && <img src={fileUrl(f.image_url)} alt="" className="w-14 h-16 object-cover rounded border"/>}
              <label className="btn-outline-silk cursor-pointer">{uploading ? "Uploading…" : f.image_url ? "Replace image" : "Upload image"}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden"/></label>
              {f.image_url && <button type="button" onClick={() => setF({...f, image_url: ""})} className="text-xs text-[#991B1B]">Remove</button>}
            </div>
          </div>
          <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">Description (optional)</label>
            <textarea rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/></div>
          <div className="border border-neutral-200 bg-neutral-50/60 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.show_in_menu} onChange={e => setF({...f, show_in_menu: e.target.checked})} data-testid="cat-menu"/>Show in the header <b>Shop</b> menu</label>
            {f.show_in_menu && <div className="max-w-[180px]"><Input label="Position in menu (1 = first)" type="number" value={f.menu_order} onChange={v => setF({...f, menu_order: v})} testid="cat-menu-order"/></div>}
          </div>
          <button disabled={busy || uploading} className="btn-silk disabled:opacity-50" data-testid="cat-save">{busy ? "Saving…" : editId ? "Save changes" : "Create category"}</button>
        </form>
        <div className="bg-white border overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["Category","Barcode prefix","Products","Shop menu",""].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
            <tbody>
              {ordered.map(c => (
                <tr key={c.id} className={`border-t ${editId === c.id ? "bg-[#E8F3EE]/60" : ""}`} data-testid={`cat-row-${c.slug}`}>
                  <td className="p-3"><div className={`flex items-center gap-2 ${c.parent_id ? "pl-5" : "font-semibold"}`}>
                    {c.parent_id && <span className="text-neutral-300">└</span>}
                    {c.image_url && <img src={fileUrl(c.image_url)} alt="" className="w-7 h-8 object-cover rounded"/>}{c.name}</div></td>
                  <td className="p-3 font-mono text-xs">{c.prefix || <span className="text-neutral-400">—</span>}</td>
                  <td className="p-3">{count(c.id)}</td>
                  <td className="p-3">{c.show_in_menu ? <span className="text-[10px] uppercase tracking-widest text-[#0E4D34] bg-[#E8F3EE] px-2 py-0.5">#{c.menu_order ?? "–"}</span> : <span className="text-neutral-300">—</span>}</td>
                  <td className="p-3"><div className="flex items-center gap-3 justify-end">
                    <button onClick={() => startEdit(c)} className="text-xs border px-3 py-1.5 flex items-center gap-1 hover:border-[#0E4D34]" data-testid={`cat-edit-${c.slug}`}><Edit2 size={12}/>Edit</button>
                    <button onClick={() => remove(c)} aria-label="Delete category" className="text-[#991B1B] p-1.5"><Trash2 size={14}/></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- INVENTORY / PIECES ----------
// ---------- SCANNER ----------
function Scanner() {
  const [code, setCode] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [action, setAction] = React.useState("lookup");
  const submit = async (e) => {
    e?.preventDefault();
    try { const r = await api.post("/pieces/scan", { barcode: code, action }); setResult(r.data); toast.success(`${action} on ${code}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); setResult(null); }
  };
  return (
    <div>
      <h1 className="h2-editorial mb-2">Barcode Scanner</h1>
      <p className="text-sm text-neutral-600 mb-6">Enter a barcode manually, or use the camera below (phone-friendly).</p>
      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-white border p-6">
          <Input label="Barcode" value={code} onChange={setCode} testid="scan-input"/>
          <Select label="Action" value={action} onChange={setAction} testid="scan-action"><option value="lookup">Lookup</option><option value="stock_in">Stock In</option><option value="stock_out">Sell / Stock Out</option></Select>
          <button className="btn-silk w-full mt-3" data-testid="btn-scan">Scan</button>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-2"><Camera size={12}/>Camera scan (mobile)</div>
            <div className="aspect-video bg-neutral-900 flex items-center justify-center text-neutral-400 text-xs">Point camera at barcode<br/>(camera scanning uses html5-qrcode - grants required)</div>
          </div>
        </form>
        <div className="bg-white border p-6 min-h-[200px]">
          <h3 className="font-semibold mb-3">Result</h3>
          {result ? (
            <div className="space-y-2 text-sm">
              <div><strong>Product:</strong> {result.product?.name}</div>
              <div><strong>Price:</strong> {inr(result.product?.price)}</div>
              <div><strong>Status:</strong> <span className="uppercase font-semibold text-[#0E4D34]">{result.piece.status}</span></div>
              <div><strong>Location:</strong> {result.piece.location}</div>
              {result.product?.images?.[0] && <img src={fileUrl(result.product.images[0])} className="w-32 aspect-[3/4] object-cover mt-2"/>}
            </div>
          ) : <p className="text-neutral-500 text-sm">Scan or lookup to see details.</p>}
        </div>
      </div>
    </div>
  );
}

// ---------- CUSTOMIZATIONS (Designer) ----------
function AdminCustomizations() {
  const { data, refetch } = useQuery({ queryKey: ["adm-cust"], queryFn: () => api.get("/customizations").then(r => r.data) });
  const [selected, setSelected] = React.useState(null);
  const update = async (id, patch) => { await api.patch(`/customizations/${id}`, patch); refetch(); toast.success("Updated"); };
  const [note, setNote] = React.useState("");
  const pg = usePaged(data || [], 15);
  return (
    <div>
      <h1 className="h2-editorial mb-6">Customization Queue</h1>
      <div className="grid lg:grid-cols-[400px_1fr] gap-4">
        <div className="space-y-2 max-h-[70vh] overflow-y-auto custom-scroll pr-2">
          {pg.items.map(c => (
            <button key={c.id} onClick={() => { setSelected(c); setNote(c.designer_notes || ""); }} data-testid={`cust-${c.id}`} className={`w-full text-left bg-white border p-4 ${selected?.id === c.id ? "border-[#0E4D34]" : ""}`}>
              <div className="flex justify-between mb-1"><span className="font-semibold">{c.title}</span><span className="text-[10px] uppercase bg-[#E8F3EE] text-[#0E4D34] px-2 py-0.5">{c.status}</span></div>
              <div className="text-xs text-neutral-500">{c.customer_name} · {c.customer_email}</div>
            </button>
          ))}
          <Pagination {...pg.props} label="requests" className="!mt-4 !items-start"/>
        </div>
        <div className="bg-white border p-6">
          {selected ? (
            <div>
              <h3 className="font-serif text-2xl mb-1">{selected.title}</h3>
              <div className="text-xs text-neutral-500 mb-3">{selected.customer_name} · {selected.customer_email} · {selected.contact_phone}</div>
              <p className="text-sm mb-4">{selected.description}</p>
              {selected.budget && <div className="text-sm mb-3">Budget: {inr(selected.budget)}</div>}
              <div className="flex gap-2 mb-4">
                {["received","in_design","approved","in_production","ready","delivered"].map(s => (
                  <button key={s} onClick={() => update(selected.id, { status: s })} data-testid={`set-${s}`} className={`text-xs px-2 py-1 border ${selected.status === s ? "bg-[#0E4D34] text-white" : ""}`}>{s.replace("_", " ")}</button>
                ))}
              </div>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Designer notes" data-testid="cust-note" className="w-full border p-2 text-sm mb-2"/>
              <button onClick={() => update(selected.id, { designer_notes: note })} className="btn-silk" data-testid="save-note">Save Notes</button>
            </div>
          ) : <p className="text-neutral-500">Select a request to view details.</p>}
        </div>
      </div>
    </div>
  );
}

// ---------- POS ----------
function POS() {
  const [code, setCode] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [phone, setPhone] = React.useState("");
  const scan = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/pieces/scan", { barcode: code, action: "lookup" });
      if (r.data.piece.status !== "in_stock") return toast.error("Piece not available");
      setItems(prev => [...prev, { ...r.data.product, piece: r.data.piece.barcode }]);
      setCode("");
    } catch { toast.error("Not found"); }
  };
  const total = items.reduce((s, i) => s + i.price, 0);
  const complete = async () => {
    for (const it of items) await api.post("/pieces/scan", { barcode: it.piece, action: "stock_out" });
    toast.success(`Sale complete: ${inr(total)}`);
    setItems([]);
  };
  return (
    <div>
      <h1 className="h2-editorial mb-6">POS-lite (In-Store)</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div>
          <form onSubmit={scan} className="bg-white border p-4 flex gap-2 mb-4">
            <input autoFocus value={code} onChange={e => setCode(e.target.value)} data-testid="pos-scan" placeholder="Scan or type barcode" className="flex-1 border p-3 font-mono"/>
            <button className="btn-silk">Add</button>
          </form>
          <div className="bg-white border">
            {items.length === 0 ? <div className="p-6 text-neutral-500 text-sm">Scan pieces to begin sale.</div> :
              items.map((it, i) => <div key={i} className="flex justify-between p-3 border-b"><span>{it.name} <span className="font-mono text-xs text-neutral-400">({it.piece})</span></span><span className="font-semibold">{inr(it.price)}</span></div>)
            }
          </div>
        </div>
        <aside className="bg-white border p-6 h-fit">
          <h3 className="font-semibold mb-3">Total</h3>
          <div className="font-serif text-4xl text-[#0E4D34] mb-4">{inr(total)}</div>
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Customer phone (opt)" className="w-full border p-2 text-sm mb-3"/>
          <button onClick={complete} disabled={items.length === 0} className="btn-silk w-full disabled:opacity-40" data-testid="pos-complete">Complete Sale</button>
        </aside>
      </div>
    </div>
  );
}

// ---------- STATISTICS ----------
function Statistics() {
  const { data } = useQuery({ queryKey: ["stats-full"], queryFn: () => api.get("/statistics").then(r => r.data) });
  if (!data) return <div>Loading...</div>;
  const COLORS = ["#0E4D34", "#991B1B", "#D4AF37", "#2D7A5B", "#7F1D1D"];
  return (
    <div>
      <h1 className="h2-editorial mb-6">Statistics</h1>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Stat label="Revenue" value={inr(data.revenue)}/>
        <Stat label="Orders" value={data.order_count}/>
        <Stat label="AOV" value={inr(data.aov)}/>
        <Stat label="Low Stock" value={data.low_stock_count}/>
      </div>
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border p-4">
          <h3 className="font-semibold mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={220}><LineChart data={data.daily_revenue}><XAxis dataKey="date"/><YAxis/><Tooltip/><Line dataKey="revenue" stroke="#0E4D34" strokeWidth={2}/></LineChart></ResponsiveContainer>
        </div>
        <div className="bg-white border p-4">
          <h3 className="font-semibold mb-4">Category Breakdown</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={data.category_breakdown} dataKey="revenue" nameKey="name" outerRadius={80} label>{data.category_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}</Pie><Tooltip/></PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white border p-4">
        <h3 className="font-semibold mb-4">Top Products</h3>
        <ResponsiveContainer width="100%" height={280}><BarChart data={data.top_products}><XAxis dataKey="name" tick={{ fontSize: 10 }}/><YAxis/><Tooltip/><Bar dataKey="revenue" fill="#0E4D34"/></BarChart></ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------- CRM ----------
function CustomersCRM() {
  const { data } = useQuery({ queryKey: ["crm-customers"], queryFn: () => api.get("/admin/customers").then(r => r.data) });
  const [q, setQ] = React.useState("");
  const filtered = (data || []).filter(c =>
    !q || c.name?.toLowerCase().includes(q.toLowerCase()) || c.email?.toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q)
  );
  const pg = usePaged(filtered, 20, { resetKey: q });
  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <h1 className="h2-editorial">Customers</h1>
          <p className="text-sm text-neutral-500 mt-1">{(data || []).length} registered customers</p>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, phone" data-testid="crm-search"
               className="border border-neutral-300 p-2.5 text-sm w-72 focus:outline-none focus:border-[#0E4D34]"/>
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-widest">
            <tr>{["Customer","Phone","Orders","Total Spent","Last Order","Joined","Actions"].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-neutral-500">No customers found.</td></tr>}
            {pg.items.map(c => (
              <tr key={c.id} className="border-t hover:bg-neutral-50" data-testid={`crm-row-${c.id}`}>
                <td className="p-3">
                  <Link to={`/admin/customers/${c.id}`} className="font-semibold hover:text-[#0E4D34]" data-testid={`crm-view-${c.id}`}>{c.name}</Link>
                  <div className="text-xs text-neutral-500">{c.email}</div>
                </td>
                <td className="p-3 font-mono text-xs">{c.phone || "-"}</td>
                <td className="p-3">{c.order_count}</td>
                <td className="p-3 font-semibold text-[#0E4D34]">{inr(c.total_spent)}</td>
                <td className="p-3 text-xs text-neutral-500">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString() : "-"}</td>
                <td className="p-3 text-xs text-neutral-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Link to={`/admin/customers/${c.id}`} className="text-[#0E4D34] hover:underline text-xs">View</Link>
                    {c.phone && (
                      <a href={waHref(c.phone)} target="_blank" rel="noreferrer"
                         data-testid={`crm-wa-${c.id}`} className="text-xs text-[#128C7E] hover:underline flex items-center gap-1">
                        <MessageCircle size={12}/>WhatsApp
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pg.props} label="customers"/>
    </div>
  );
}

function CustomerDetailCRM() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["crm-customer", id], queryFn: () => api.get(`/admin/customers/${id}`).then(r => r.data) });
  const [phone, setPhone] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [editing, setEditing] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  React.useEffect(() => {
    if (data) {
      setPhone(data.customer.phone || "");
      setNotes(data.customer.notes || "");
      setMsg(`Hi ${data.customer.name?.split(" ")[0] || "there"}, this is Avishka Navya Collections. `);
    }
  }, [data]);

  if (!data) return <div>Loading...</div>;
  const c = data.customer;

  const saveDetails = async () => {
    await api.patch(`/admin/customers/${id}`, { phone: phone || null, notes: notes || null });
    refetch();
    setEditing(false);
    toast.success("Customer details saved");
  };

  const sendWhatsApp = async () => {
    const digits = (phone || "").replace(/\D/g, "");
    if (!digits) return toast.error("No phone number on file");
    window.open(waHref(digits, msg), "_blank");
    await api.post(`/admin/customers/${id}/messages`, { body: msg });
    refetch();
    toast.success("WhatsApp opened & message logged");
  };

  return (
    <div>
      <Link to="/admin/customers" className="text-xs text-neutral-500 hover:text-[#0E4D34] mb-2 inline-block">← All Customers</Link>
      <div className="flex justify-between items-start flex-wrap gap-4 mb-6">
        <div>
          <h1 className="h2-editorial">{c.name}</h1>
          <div className="text-sm text-neutral-500">{c.email}</div>
        </div>
        <span className="text-xs uppercase tracking-widest px-3 py-1.5 bg-[#E8F3EE] text-[#0E4D34] font-semibold" data-testid="crm-ltv">
          Lifetime Value {inr(data.total_spent)}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <div className="bg-white border p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Profile</h3>
              <button onClick={() => editing ? saveDetails() : setEditing(true)} className="text-xs text-[#0E4D34] flex items-center gap-1" data-testid="crm-edit-profile">
                <Edit2 size={12}/>{editing ? "Save" : "Edit"}
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div><span className="text-xs uppercase tracking-widest text-neutral-500 block">Phone</span>
                {editing ? <input value={phone} onChange={e => setPhone(e.target.value)} data-testid="crm-phone-input" className="border p-2 w-full text-sm" placeholder="+91 ..."/> : <span className="font-mono">{c.phone || "Not on file"}</span>}
              </div>
              <div><span className="text-xs uppercase tracking-widest text-neutral-500 block">Joined</span>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</div>
              <div><span className="text-xs uppercase tracking-widest text-neutral-500 block">Notes</span>
                {editing ? <textarea value={notes} onChange={e => setNotes(e.target.value)} data-testid="crm-notes-input" rows={3} className="border p-2 w-full text-sm"/> : <span className="text-neutral-600">{c.notes || "—"}</span>}
              </div>
            </div>
          </div>

          <div className="bg-white border p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><MessageCircle size={16} className="text-[#128C7E]"/>Send WhatsApp</h3>
            <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={4} data-testid="crm-wa-message"
                      className="w-full border p-3 text-sm mb-3" placeholder="Type your message..."/>
            <button onClick={sendWhatsApp} data-testid="crm-wa-send" className="w-full bg-[#128C7E] text-white hover:bg-[#0d6e62] font-semibold text-xs tracking-widest uppercase py-3 transition-colors">
              Send via WhatsApp
            </button>
            {!c.phone && <p className="text-xs text-[#991B1B] mt-2">Add a phone number first (Edit profile above).</p>}
          </div>

          {(data.messages || []).length > 0 && (
            <div className="bg-white border p-5">
              <h3 className="font-semibold mb-3">Message History</h3>
              <div className="space-y-2 max-h-56 overflow-y-auto custom-scroll">
                {data.messages.map(m => (
                  <div key={m.id} className="text-xs border-b pb-2">
                    <div className="text-neutral-400">{new Date(m.at).toLocaleString()} · by {m.sent_by}</div>
                    <div className="text-neutral-700 mt-0.5">{m.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border">
            <div className="p-4 border-b font-semibold flex justify-between"><span>Orders ({data.orders.length})</span></div>
            {data.orders.length === 0 ? <div className="p-6 text-sm text-neutral-500">No orders yet.</div> :
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["Order","Items","Total","Payment","Status","Date"].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
                <tbody>
                  {data.orders.map(o => (
                    <tr key={o.id} className="border-t" data-testid={`crm-order-${o.order_number}`}>
                      <td className="p-3 font-mono text-xs">{o.order_number}</td>
                      <td className="p-3 text-xs">{o.items.map(i => `${i.name} ×${i.quantity}`).join(", ")}</td>
                      <td className="p-3 font-semibold text-[#0E4D34]">{inr(o.total)}</td>
                      <td className="p-3 text-xs">{o.payment_method} · {o.payment_status}</td>
                      <td className="p-3"><span className="text-[10px] uppercase px-2 py-0.5 bg-[#E8F3EE] text-[#0E4D34]">{o.status}</span></td>
                      <td className="p-3 text-xs text-neutral-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>}
          </div>

          {(data.customizations || []).length > 0 && (
            <div className="bg-white border">
              <div className="p-4 border-b font-semibold">Customization Requests ({data.customizations.length})</div>
              {data.customizations.map(cu => (
                <div key={cu.id} className="p-4 border-b last:border-0 text-sm">
                  <div className="flex justify-between"><span className="font-semibold">{cu.title}</span>
                    <span className="text-[10px] uppercase px-2 py-0.5 bg-[#E8F3EE] text-[#0E4D34]">{cu.status}</span></div>
                  <div className="text-xs text-neutral-500 mt-1">{new Date(cu.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}

          {(data.wishlist || []).length > 0 && (
            <div className="bg-white border p-4">
              <h3 className="font-semibold mb-3">Wishlist ({data.wishlist.length})</h3>
              <div className="flex flex-wrap gap-2">
                {data.wishlist.map(w => (
                  <Link key={w.slug} to={`/product/${w.slug}`} target="_blank" className="text-xs border px-3 py-1.5 hover:border-[#0E4D34]">{w.name} · {inr(w.price)}</Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- USERS ----------
function UsersAdmin() {
  const { data, refetch } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then(r => r.data) });
  const { data: roles } = useQuery({ queryKey: ["roles"], queryFn: () => api.get("/roles").then(r => r.data) });
  const [f, setF] = React.useState({ email: "", password: "", name: "", role: "admin" });
  const add = async (e) => { e.preventDefault(); try { await api.post("/users", f); setF({ email: "", password: "", name: "", role: "admin" }); refetch(); toast.success("Created"); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const updateRole = async (id, role) => { await api.patch(`/users/${id}`, { role }); refetch(); };
  const togglePerm = async (u, p) => {
    const perms = (u.permissions || []).includes(p) ? u.permissions.filter(x => x !== p) : [...(u.permissions || []), p];
    await api.patch(`/users/${u.id}`, { permissions: perms }); refetch();
  };
  const [uq, setUq] = React.useState("");
  const users = (data || []).filter(u => !uq || [u.name, u.email, u.role].some(v => (v || "").toLowerCase().includes(uq.toLowerCase())));
  const pg = usePaged(users, 15, { resetKey: uq });
  return (
    <div>
      <h1 className="h2-editorial mb-6">Users & Roles</h1>
      <form onSubmit={add} className="bg-white border p-6 grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Input label="Name" value={f.name} onChange={v => setF({...f, name: v})} testid="u-name"/>
        <Input label="Email" type="email" value={f.email} onChange={v => setF({...f, email: v})} testid="u-email"/>
        <Input label="Password" type="password" value={f.password} onChange={v => setF({...f, password: v})} testid="u-password"/>
        <div className="flex flex-col gap-1">
          <Select label="Role" value={f.role} onChange={v => setF({...f, role: v})} testid="u-role">{(roles?.roles || []).map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</Select>
        </div>
        <button className="btn-silk col-span-full md:col-span-1 md:col-start-4" data-testid="u-create">Create User</button>
      </form>
      <div className="flex justify-end mb-3"><input value={uq} onChange={e => setUq(e.target.value)} placeholder="Search users" className="border border-neutral-300 p-2.5 text-sm w-60"/></div>
      <div className="space-y-3">
        {pg.items.map(u => (
          <div key={u.id} className="bg-white border p-4">
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div>
                <div className="font-semibold">{u.name} <span className="text-xs text-neutral-500">({u.email})</span></div>
                <select value={u.role} onChange={e => updateRole(u.id, e.target.value)} className="text-xs border p-1 mt-1">{(roles?.roles || []).map(r => <option key={r}>{r}</option>)}</select>
              </div>
              <div className="text-xs flex flex-wrap gap-1 max-w-md">
                {(roles?.permissions || []).map(p => (
                  <button key={p} type="button" onClick={() => togglePerm(u, p)} className={`px-2 py-1 border text-[10px] ${(u.permissions || []).includes(p) ? "bg-[#0E4D34] text-white" : "text-neutral-600"}`}>{p}</button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Pagination {...pg.props} label="users"/>
    </div>
  );
}

// ---------- COUPONS ----------
function Coupons() {
  const { data, refetch } = useQuery({ queryKey: ["cpn"], queryFn: () => api.get("/coupons").then(r => r.data) });
  const [f, setF] = React.useState({ code: "", discount_percent: "", discount_amount: "", min_order: 0, active: true });
  const pg = usePaged(data || [], 20);
  const add = async (e) => { e.preventDefault(); await api.post("/coupons", { ...f, discount_percent: f.discount_percent ? Number(f.discount_percent) : null, discount_amount: f.discount_amount ? Number(f.discount_amount) : null, min_order: Number(f.min_order || 0) }); setF({ code: "", discount_percent: "", discount_amount: "", min_order: 0, active: true }); refetch(); };
  return (
    <div>
      <h1 className="h2-editorial mb-6">Coupons</h1>
      <form onSubmit={add} className="bg-white border p-6 grid md:grid-cols-4 gap-3 mb-6">
        <Input label="Code" value={f.code} onChange={v => setF({...f, code: v.toUpperCase()})} testid="cpn-code"/>
        <Input label="% Off" type="number" value={f.discount_percent} onChange={v => setF({...f, discount_percent: v})}/>
        <Input label="₹ Off" type="number" value={f.discount_amount} onChange={v => setF({...f, discount_amount: v})}/>
        <Input label="Min Order" type="number" value={f.min_order} onChange={v => setF({...f, min_order: v})}/>
        <button className="btn-silk md:col-start-4">Add Coupon</button>
      </form>
      <div className="bg-white border">
        {(data || []).length === 0 && <div className="p-6 text-sm text-neutral-500">No coupons yet.</div>}
        {pg.items.map(c => <div key={c.code} className="p-3 border-b flex justify-between"><span className="font-mono">{c.code} — {c.discount_percent ? `${c.discount_percent}%` : `₹${c.discount_amount}`}</span><button onClick={async () => { await api.delete(`/coupons/${c.code}`); refetch(); }} className="text-[#991B1B]"><Trash2 size={14}/></button></div>)}
      </div>
      <Pagination {...pg.props} label="coupons"/>
    </div>
  );
}

// ---------- SETTINGS ----------
function AdminSettings() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then(r => r.data) });
  const [f, setF] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (data) setF(data); }, [data]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const keys = ["multi_location_enabled", "cod_enabled", "razorpay_enabled", "low_stock_threshold", "business_name", "business_gstin",
        "business_address", "business_phone", "business_whatsapp", "business_email", "business_hours", "instagram_url", "facebook_url", "youtube_url"];
      await api.patch("/settings", Object.fromEntries(keys.filter(k => f[k] !== undefined).map(k => [k, typeof f[k] === "string" ? f[k].trim() : f[k]])));
      refetch(); qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  // plain function (not a component) so typing doesn't remount the textarea and lose focus
  const area = (label, k, rows = 3, testid) => (
    <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">{label}</label>
      <textarea rows={rows} value={f[k] || ""} onChange={e => set(k)(e.target.value)} data-testid={testid} className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/></div>
  );
  return (
    <div>
      <h1 className="h2-editorial mb-6">Settings</h1>
      <div className="grid xl:grid-cols-2 gap-6 items-start max-w-6xl">
        <div className="bg-white border p-6 space-y-4">
          <div>
            <h3 className="font-semibold">Store contact details</h3>
            <p className="text-xs text-neutral-500 mt-1">Shown on the Contact page, the home page "Visit our Boutique" section and the footer.</p>
          </div>
          <Input label="Business name" value={f.business_name || ""} onChange={set("business_name")}/>
          {area("Address (press Enter for a new line)", "business_address", 3, "s-address")}
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Phone" value={f.business_phone || ""} onChange={set("business_phone")} testid="s-phone"/>
            <Input label="WhatsApp number" value={f.business_whatsapp || ""} onChange={set("business_whatsapp")} testid="s-whatsapp"/>
          </div>
          <Input label="Email" type="email" value={f.business_email || ""} onChange={set("business_email")} testid="s-email"/>
          {area("Store hours", "business_hours", 2, "s-hours")}
          <div className="grid sm:grid-cols-3 gap-3">
            <Input label="Instagram link" value={f.instagram_url || ""} onChange={set("instagram_url")}/>
            <Input label="Facebook link" value={f.facebook_url || ""} onChange={set("facebook_url")}/>
            <Input label="YouTube link" value={f.youtube_url || ""} onChange={set("youtube_url")}/>
          </div>
        </div>
        <div className="bg-white border p-6 space-y-4">
          <h3 className="font-semibold">Store & payments</h3>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.multi_location_enabled} onChange={e => set("multi_location_enabled")(e.target.checked)} data-testid="s-multi"/>Multi-location inventory</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.cod_enabled} onChange={e => set("cod_enabled")(e.target.checked)}/>Cash on Delivery</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.razorpay_enabled} onChange={e => set("razorpay_enabled")(e.target.checked)}/>Razorpay</label>
          <div className={`text-xs p-2.5 ${data?.razorpay_configured ? "bg-[#E8F3EE] text-[#0E4D34]" : "bg-[#FFF7E0] text-[#8a6d00]"}`} data-testid="razorpay-status">
            {data?.razorpay_configured
              ? <>Razorpay keys are set up ({data.razorpay_mode === "live" ? "LIVE mode — real payments" : "TEST mode — no real money"}). Online payment is shown at checkout.</>
              : <>Razorpay keys are not set yet — customers will only see Cash on Delivery. Add RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET to the server's backend/.env and restart.</>}
          </div>
          <Input label="Low stock threshold" type="number" value={f.low_stock_threshold || 0} onChange={v => set("low_stock_threshold")(Number(v))}/>
          <Input label="GSTIN (printed on invoices)" value={f.business_gstin || ""} onChange={set("business_gstin")}/>
        </div>
      </div>
      <button onClick={save} disabled={busy} className="btn-silk mt-6 disabled:opacity-50" data-testid="s-save">{busy ? "Saving…" : "Save settings"}</button>
      <div className="mt-8"><ShippingSettings/></div>
    </div>
  );
}

// ---------- REVIEWS (moderation) ----------
function ReviewsAdmin() {
  const { data, refetch, isLoading } = useQuery({ queryKey: ["adm-reviews"], queryFn: () => api.get("/admin/reviews").then(r => r.data) });
  const [q, setQ] = React.useState("");
  const [stars, setStars] = React.useState("");
  const list = (data || []).filter(r => (!stars || String(r.rating) === stars) &&
    (!q || [r.product_name, r.user_name, r.title, r.body].some(v => (v || "").toLowerCase().includes(q.toLowerCase()))));
  const pg = usePaged(list, 15, { resetKey: `${q}|${stars}` });
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.user_name}'s review of "${r.product_name}"?`)) return;
    try { await api.delete(`/reviews/${r.id}`); toast.success("Review deleted"); refetch(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const avg = (data || []).length ? ((data || []).reduce((t, r) => t + r.rating, 0) / data.length).toFixed(1) : "–";
  return (
    <div>
      <div className="flex justify-between items-end gap-3 flex-wrap mb-6">
        <div>
          <h1 className="h2-editorial">Reviews</h1>
          <p className="text-sm text-neutral-500 mt-1">{(data || []).length} reviews · average {avg} ★ — delete anything abusive or spam.</p>
        </div>
        <div className="flex gap-2">
          <select value={stars} onChange={e => setStars(e.target.value)} className="border border-neutral-300 p-2.5 text-sm bg-white">
            <option value="">All ratings</option>{[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} ★</option>)}
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search" className="border border-neutral-300 p-2.5 text-sm w-44 sm:w-60"/>
        </div>
      </div>
      <div className="bg-white border divide-y">
        {isLoading && <div className="p-6 text-sm text-neutral-500">Loading…</div>}
        {!isLoading && list.length === 0 && <div className="p-6 text-sm text-neutral-500">No reviews.</div>}
        {pg.items.map(r => (
          <div key={r.id} className="p-4 flex gap-4 items-start" data-testid="admin-review-row">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="text-[#D4AF37] tracking-tight">{"★".repeat(r.rating)}<span className="text-neutral-300">{"★".repeat(5 - r.rating)}</span></span>
                <span className="font-semibold">{r.user_name}</span>
                {r.verified && <span className="text-[10px] uppercase tracking-widest text-[#0E4D34] bg-[#E8F3EE] px-1.5 py-0.5">Verified buyer</span>}
                <span className="text-xs text-neutral-400">{new Date(r.updated_at || r.created_at).toLocaleDateString()}</span>
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">on {r.product_slug ? <Link to={`/product/${r.product_slug}`} target="_blank" className="underline hover:text-[#0E4D34]">{r.product_name}</Link> : r.product_name}</div>
              {r.title && <div className="font-semibold text-sm mt-1.5">{r.title}</div>}
              {r.body && <p className="text-sm text-neutral-700 mt-0.5 whitespace-pre-line break-words">{r.body}</p>}
            </div>
            <button onClick={() => remove(r)} aria-label="Delete review" className="text-[#991B1B] p-1.5"><Trash2 size={15}/></button>
          </div>
        ))}
      </div>
      <Pagination {...pg.props} label="reviews"/>
    </div>
  );
}

// helpers
function Input({ label, value, onChange, type = "text", testid }) {
  return <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">{label}</label><input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/></div>;
}
function Select({ label, value, onChange, children, testid }) {
  return <div><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">{label}</label><select value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full border border-neutral-300 p-2.5 text-sm bg-white">{children}</select></div>;
}
